if (!customElements.get("m-commercial-invoice")) {
  const viewCartDraftKey = "minimog:commercial-invoice:view-cart";

  class MCommercialInvoice extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.toggle = this.querySelector("[data-commercial-invoice-toggle]");
      this.invoiceType = this.querySelector("[data-commercial-invoice-type]");
      this.fields = this.querySelector("[data-commercial-invoice-fields]");
      this.inputs = Array.from(this.querySelectorAll("[data-commercial-invoice-input]"));
      this.error = this.querySelector("[data-commercial-invoice-error]");
      this.scope = this.closest("[data-commercial-invoice-scope]");
      this.checkoutButton = this.scope && this.scope.querySelector('button[name="checkout"]');
      if (!this.toggle || !this.invoiceType || !this.fields || !this.checkoutButton) return;

      this.handleToggle = this.handleToggle.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);

      this.toggle.addEventListener("change", this.handleToggle);
      this.inputs.forEach((input) => input.addEventListener("input", this.handleInput));
      // Cart drawer updates replace the form while keeping this component connected.
      this.ownerDocument.addEventListener("submit", this.handleSubmit, true);
      window.addEventListener("pageshow", this.handlePageShow);

      if (!this.hasLoaded) {
        this.startupDraft = this.takeViewCartDraft();
        this.restoreStartupState();
        this.hasLoaded = true;
      }
      this.initialized = true;
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.toggle.removeEventListener("change", this.handleToggle);
      this.inputs.forEach((input) => input.removeEventListener("input", this.handleInput));
      this.ownerDocument.removeEventListener("submit", this.handleSubmit, true);
      window.removeEventListener("pageshow", this.handlePageShow);
      this.initialized = false;
    }

    handleToggle() {
      this.hasUserInput = true;
      const expanded = this.toggle.checked;
      this.setExpanded(expanded);
      this.clearError();
    }

    handleInput(event) {
      this.hasUserInput = true;
      if (event.target.hasAttribute("data-commercial-invoice-tax-number")) {
        event.target.value = event.target.value.replace(/\D/g, "").slice(0, 10);
      }

      event.target.setCustomValidity("");
      this.clearError();
    }

    handleSubmit(event) {
      if (event.target !== this.checkoutButton.form) return;

      const submitter = event.submitter || this.checkoutButton;
      if (submitter && submitter.hasAttribute("data-commercial-invoice-view-cart")) {
        this.saveViewCartDraft();
        return;
      }
      if (!submitter || submitter.name !== "checkout") return;

      if (!this.validate()) {
        event.preventDefault();
        return;
      }

      const agreement = this.scope && this.scope.querySelector('[name="agree_terms"]');
      if (!agreement || agreement.checked) return;

      event.preventDefault();
      agreement.reportValidity();
    }

    handlePageShow(event) {
      if (event.persisted) {
        this.startupDraft = null;
        this.hasUserInput = false;
      }
      if (!this.hasUserInput) this.restoreStartupState();
    }

    restoreStartupState() {
      this.toggle.checked = Boolean(this.startupDraft);
      this.setExpanded(this.toggle.checked, false);
      if (this.startupDraft) {
        this.inputs.forEach((input, index) => {
          input.value = this.startupDraft.values[index];
        });
      }
      this.clearError();
    }

    takeViewCartDraft() {
      // Only an explicit View cart submission carries details to the next page.
      // Saved cart attributes and browser refreshes never select the checkbox.
      try {
        const draft = JSON.parse(sessionStorage.getItem(viewCartDraftKey));
        sessionStorage.removeItem(viewCartDraftKey);
        const navigation = performance.getEntriesByType("navigation")[0];
        if (
          draft && navigation && navigation.type === "navigate" &&
          draft.path === window.location.pathname && draft.expires > Date.now() &&
          Array.isArray(draft.values) && draft.values.length === this.inputs.length &&
          draft.values.every((value) => typeof value === "string")
        ) return draft;
      } catch (error) {
        // Storage can be unavailable; keep the default individual checkout usable.
      }
      return null;
    }

    saveViewCartDraft() {
      try {
        sessionStorage.removeItem(viewCartDraftKey);
        if (!this.toggle.checked) return;
        sessionStorage.setItem(viewCartDraftKey, JSON.stringify({
          path: new URL(this.checkoutButton.form.action).pathname,
          expires: Date.now() + 60000,
          values: this.inputs.map((input) => input.value),
        }));
      } catch (error) {
        // The cart form still submits normally when browser storage is unavailable.
      }
    }

    setExpanded(expanded, focusFirstField = true) {
      this.toggle.setAttribute("aria-expanded", String(expanded));
      this.invoiceType.value = expanded ? "Kurumsal" : "Bireysel";
      this.fields.hidden = !expanded;
      this.inputs.forEach((input) => {
        input.required = expanded;
        input.setCustomValidity("");

        if (!expanded) {
          input.value = "";
        }
      });
      this.scope && this.scope.classList.toggle("m-commercial-invoice-active", expanded);

      if (expanded && focusFirstField) {
        requestAnimationFrame(() => this.inputs[0] && this.inputs[0].focus());
      }
    }

    validate() {
      if (!this.toggle.checked) return true;

      const invalidInput = this.inputs.find((input) => {
        input.value = input.value.trim();
        input.setCustomValidity(input.value ? "" : this.dataset.validationError);
        return !input.checkValidity();
      });
      if (!invalidInput) return true;

      this.showError(this.dataset.validationError);
      invalidInput.reportValidity();
      return false;
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      if (!this.error) return;
      this.error.textContent = "";
      this.error.hidden = true;
    }
  }

  customElements.define("m-commercial-invoice", MCommercialInvoice);
}
